/**
 * "The full text", one tap away from an ask (CS-D16; `R2-full-text` as a dialog, which the decisions
 * document says to build for the web: "the same content in a dialog").
 *
 * The screen carries a question and one or two sentences. Everything the person has to be able to
 * read before deciding is here: four numbered facts (what, who, where, why) and two closing
 * sentences on what a no costs and how to withdraw. It is never more than one tap from the ask and
 * never only on the website (Tesler's Law, in the document), and it has no count of its own: it is
 * the lawyer's text (#403), word for word the deck's.
 *
 * Opened by the chip under the ask ("What exactly is sent"). Escape, the scrim and "Done" close it;
 * focus goes into it on open and back to the chip on close, the page behind is inert while it is open
 * (`useModal`), and Tab stays inside, between "Done" and the text. **The text is a tab stop of its own**
 * (a labelled region): a scrolling area a keyboard cannot reach is a text a keyboard cannot read on a
 * phone, where it is taller than the window, and once Tab had moved focus to "Done" there was no key
 * left that scrolled it. It answers nothing: reading is not an answer.
 */

import { useId, useRef } from 'react'

import { COPY, fill } from '../../copy/firstRun'
import { TOKENS } from '../ui/dashboard-primitives'
import { Dialog } from '../ui/Dialog'
import type { FullText } from './consentCopy'
import { FOCUS_RING, FOCUS_RING_INSET } from '../ui/focusRing'

/** The artboard's body text sits between the design system's "soft" and the cream itself. */
const BODY_COLOR = 'color-mix(in oklab, var(--text-cream) 86%, transparent)'

interface Props {
  text: FullText
  open: boolean
  onClose: () => void
}

/**
 * One paragraph of a fact. In a fact given as several parts each leads with its label, the words up to and including
 * the colon, set in the heavier weight; the string stays one string of the deck (AA-D3).
 */
function LabelledPart({ text, labelled }: { text: string; labelled: boolean }) {
  const colon = text.indexOf(':')
  if (!labelled || colon === -1) return <>{text}</>
  return (
    <>
      <span className="font-medium text-(--text-cream)">{text.slice(0, colon + 1)}</span>
      {text.slice(colon + 1)}
    </>
  )
}

export function ConsentFullText({ text, open, onClose }: Props) {
  const titleId = useId()
  const leadId = useId()
  const scrollRef = useRef<HTMLDivElement>(null)

  // The text takes focus, so a screen reader announces the dialog and arrow keys scroll the text (a key
  // scrolls the focused element's own scroller, not a parent that does not scroll). The page behind is
  // inert while this is open, including the ask this may be stacked on. Escape leaves, and Tab stays
  // between its two stops, "Done" and the text.
  return (
    <Dialog
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      initialFocus={scrollRef}
      // the artboard's scrim
      scrim="rgba(12, 9, 8, 0.6)"
      className="flex max-h-[calc(100dvh-2rem)] w-full max-w-[520px] flex-col overflow-hidden rounded-[20px] border border-(--text-cream-border)"
      style={{
        backgroundColor: '#1f1b19',
        boxShadow: '0 24px 48px -20px rgba(20, 14, 12, 0.55)',
        fontFamily: 'var(--font-body)',
        color: TOKENS.cream,
      }}
    >
      {/* The head stays where it is while the text scrolls: on a phone the text is taller than the
          window, and "Done" must not scroll out of reach at the moment someone finishes reading. */}
      <div className="px-6 pb-3 pt-6 sm:px-8 sm:pt-8">
        <div className="flex items-center justify-between gap-3">
          {/* Tighter on a phone, where "ANALYTICS COOKIES · VERSION 2" beside "Done" left the 2 alone on a second line. */}
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-(--text-cream-soft) sm:tracking-[0.28em]">
            {fill(COPY.consentFullTextEyebrow, { name: text.name.toUpperCase(), n: text.version })}
          </p>
          <button
            type="button"
            onClick={onClose}
            className={`-mr-1 min-h-11 px-1 text-base font-medium text-(--accent-amber) transition-colors duration-150 hover:text-(--accent-amber-light) ${FOCUS_RING}`}
          >
            {COPY.consentDone}
          </button>
        </div>
        <h2
          id={titleId}
          // A heading takes focus when a screen reader is sent to it, never in the tab order (AA review).
          tabIndex={-1}
          className="mt-1.5 text-2xl font-light leading-[1.16] tracking-tight outline-none"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {text.title ?? COPY.consentFullTextTitle}
        </h2>
      </div>

      <div
        ref={scrollRef}
        // A scrolling area is a control a keyboard has to be able to reach: a tab stop of its own, named by
        // the title, with a ring drawn inside the panel (the panel clips an outer one).
        role="region"
        aria-labelledby={titleId}
        tabIndex={0}
        // No `outline-none` beside the ring: in Tailwind 4 it sets the style the ring's `outline` reads, and the
        // ring never drew (measured: focus-visible, no outline). The browser draws none for a mouse click anyway.
        className={`min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-2 sm:px-8 sm:pb-8 ${FOCUS_RING_INSET}`}
      >
        {text.lead && (
          // What needs no agreement comes first (the analytics text: the cookie that keeps you signed in).
          <section aria-labelledby={leadId} className="flex flex-col gap-1.5">
            <h3 id={leadId} className="text-lg font-medium leading-snug">
              {text.lead.title}
            </h3>
            <p className="text-sm leading-[1.45]" style={{ color: BODY_COLOR }}>
              {text.lead.text}
            </p>
          </section>
        )}
        {text.lead && <div aria-hidden="true" className="my-5 h-px" style={{ background: TOKENS.hairlineStrong }} />}
        {text.factsTitle && (
          <h3 className="mb-4 text-lg font-medium leading-snug">{text.factsTitle}</h3>
        )}
        <ol role="list" className="flex flex-col gap-4">
          {text.facts.map((fact, index) => (
            <li key={fact.label} className="flex flex-col gap-1.5">
              <div className="flex gap-2 font-mono text-[11px] tracking-[0.14em]">
                <span className="text-(--accent-amber)">{String(index + 1).padStart(2, '0')}</span>
                <span aria-hidden="true" className="text-(--text-cream-faint)">/</span>
                <span className="text-(--text-cream-soft)">{fact.label}</span>
              </div>
              {(fact.parts ?? [fact.text ?? '']).map((part) => (
                <p key={part} className="text-sm leading-[1.45]" style={{ color: BODY_COLOR }}>
                  <LabelledPart text={part} labelled={fact.parts !== undefined} />
                </p>
              ))}
            </li>
          ))}
        </ol>

        <div aria-hidden="true" className="mt-5 h-px" style={{ background: TOKENS.hairlineStrong }} />
        {text.closing.map((sentence) => (
          <p key={sentence} className="mt-3 text-[13px] leading-normal text-(--text-cream-soft)">
            {sentence}
          </p>
        ))}
      </div>
    </Dialog>
  )
}
